import { useState, useEffect } from "react";

/**
 * Loads Razorpay Checkout — a script the guest's browser fetches once, not
 * an npm dependency (the same "thin direct integration, no SDK" approach
 * backend/services/paymentGateway.js takes server-side).
 *
 * One copy, used by both places a guest can pay: the public booking form
 * (PublicBooking.jsx, when the tenant requires payment before a booking is
 * accepted) and the post-approval guest form (GuestFormPage.jsx).
 *
 * Returns { ready, failed } rather than a bare boolean, because those are
 * genuinely different states and conflating them hid a real production
 * bug: the tenant nginx CSP allowed scripts only from 'self', so
 * checkout.razorpay.com was blocked outright, onload never fired, and the
 * pay button sat on "Payment is still loading — please try again in a
 * moment" forever. A guest retrying that message has no way to know it will
 * never succeed. `failed` lets the caller say something true instead.
 */
export function useRazorpayScript(needed) {
  const [ready, setReady] = useState(!!window.Razorpay);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!needed || window.Razorpay) return;
    // Reuse an existing tag rather than appending another on a remount.
    const existing = document.querySelector("script[data-razorpay]");
    if (existing) {
      existing.addEventListener("load", () => setReady(true));
      existing.addEventListener("error", () => setFailed(true));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.dataset.razorpay = "1";
    script.onload  = () => setReady(true);
    script.onerror = () => setFailed(true);
    document.body.appendChild(script);
  }, [needed]);

  return { ready, failed };
}

// Shown when the script never loads — a blocked CSP, an ad blocker, or a
// dead network all land here, and none of them resolve by waiting.
export const RAZORPAY_BLOCKED_MESSAGE =
  "Online payment couldn't load — it may be blocked by your browser or an extension. " +
  "Please try another browser, or contact us to book directly.";
